import React, { useState } from 'react';
import { Check, ChevronDown, CircleAlert, CircleStop } from 'lucide-react';
import { cn } from '@dashboard/lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@dashboard/components/ui/collapsible';
import { LoaderIcon } from '@dashboard/components/ui/loader-icon';
import { stepLabel, visibleSteps } from './toolStepLabels';

// self-start sizes the sweep to the words; stretched across the column it would pass over empty space.
const WORKING_CLASS =
  'self-start text-[13px] leading-5 text-muted-foreground motion-safe:animate-shimmer motion-safe:bg-[linear-gradient(90deg,var(--muted-foreground)_35%,color-mix(in_oklab,var(--muted-foreground)_35%,var(--background))_50%,var(--muted-foreground)_65%)] motion-safe:bg-size-[200%_100%] motion-safe:bg-clip-text motion-safe:text-transparent';

const STATUS_ICONS = {
  running: { label: 'Running', icon: <LoaderIcon className="size-3.5 text-muted-foreground" /> },
  done: { label: 'Done', icon: <Check className="size-3.5 text-green-600" strokeWidth={2.5} /> },
  stopped: { label: 'Stopped', icon: <CircleStop className="size-3.5 text-muted-foreground" /> },
  failed: { label: 'Failed', icon: <CircleAlert className="size-3.5 text-destructive/80" /> },
};

function Steps({ steps, pageType }) {
  return (
    <ol className="flex flex-col gap-1.5 text-[13px] leading-[18px]">
      {steps.map(step => {
        const status = STATUS_ICONS[step.status] ?? STATUS_ICONS.done;
        return (
          <li
            key={step.id}
            className={cn(
              'flex items-center gap-2',
              step.status === 'running' ? 'font-medium text-foreground' : 'text-muted-foreground',
            )}
          >
            <span role="img" aria-label={status.label} className="flex shrink-0">
              {status.icon}
            </span>
            <span>{stepLabel(step, pageType)}</span>
          </li>
        );
      })}
    </ol>
  );
}

export default function StepList({ steps: allSteps, running, keepFailedWrites, pageType }) {
  const [open, setOpen] = useState(false);
  const visible = visibleSteps(allSteps, { running, keepFailedWrites });

  if (running) {
    return (
      <div className="flex flex-col gap-2">
        <span role="status" className={WORKING_CLASS}>
          Working on it
        </span>
        {visible.length > 0 && <Steps steps={visible} pageType={pageType} />}
      </div>
    );
  }

  // A change that never went through stays in view rather than folded away with the rest.
  const failed = visible.filter(step => step.status === 'failed');
  const steps = visible.filter(step => step.status !== 'failed');
  const failedSteps = failed.length > 0 && <Steps steps={failed} pageType={pageType} />;

  if (steps.length === 0) return failedSteps || null;

  const collapsible = (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="inline-flex items-center gap-1 rounded-sm text-[13px] leading-5 text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring">
        {open ? 'Hide steps' : `Show ${steps.length} ${steps.length === 1 ? 'step' : 'steps'}`}
        <ChevronDown
          aria-hidden="true"
          className={cn('size-3.5 transition-transform', open && 'rotate-180')}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2">
        <Steps steps={steps} pageType={pageType} />
      </CollapsibleContent>
    </Collapsible>
  );

  if (!failedSteps) return collapsible;
  return (
    <div className="flex flex-col gap-2">
      {collapsible}
      {failedSteps}
    </div>
  );
}
